import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Radio, Upload, Users, Video, VideoOff, Volume2, VolumeX } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const STATUS_POLL_MS = 5000;
const PREVIEW_EVERY_MS = 3000;
const RECONNECT_MS = 3000;
const FALLBACK_ICE = [{ urls: 'stun:stun.l.google.com:19302' }];

const authHeaders = () => {
  const token = localStorage.getItem('titelli_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const wsUrl = () => {
  if (BACKEND_URL) return `${BACKEND_URL.replace(/^http/, 'ws')}/api/live/ws`;
  return `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/live/ws`;
};

const mediaUrl = (path) => {
  if (!path) return null;
  return path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
};

const formatPrice = (price) => Number(price || 0).toFixed(2).replace(/\.00$/, '');

const EMPTY_STATUS = {
  is_live: false,
  title: '',
  price: 0,
  offline_image_url: null,
  preview: null,
  viewer_count: 0,
  has_access: false,
  is_coach: false,
};

const LiveStreamSection = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const userId = auth?.user?.id;

  const [status, setStatus] = useState(EMPTY_STATUS);
  const [paying, setPaying] = useState(false);

  // Côté spectateur
  const remoteVideoRef = useRef(null);
  const [connState, setConnState] = useState('idle');
  const [muted, setMuted] = useState(true);
  const [retry, setRetry] = useState(0);

  // Côté coach
  const localVideoRef = useRef(null);
  const streamRef = useRef(null);
  const wsRef = useRef(null);
  const peersRef = useRef(new Map());
  const previewTimerRef = useRef(null);
  const formReadyRef = useRef(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [form, setForm] = useState({ title: '', price: '' });

  // ---------- état du live (toutes les 5 s) ----------

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/live/status`, { headers: authHeaders() });
      setStatus(data);
    } catch (e) {
      // serveur injoignable : on garde le dernier état connu
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, STATUS_POLL_MS);
    return () => clearInterval(id);
  }, [fetchStatus, userId]);

  // Les champs du coach se remplissent une seule fois avec les derniers réglages
  useEffect(() => {
    if (status.is_coach && !formReadyRef.current) {
      formReadyRef.current = true;
      setForm({ title: status.title || '', price: status.price ? String(status.price) : '' });
    }
  }, [status.is_coach, status.title, status.price]);

  // ---------- retour de Stripe ----------

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paidSession = params.get('live_paid');
    const cancelled = params.get('live_cancelled');
    if (!paidSession && !cancelled) return;
    window.history.replaceState({}, '', window.location.pathname);
    if (cancelled) {
      toast.info('Paiement annulé');
      return;
    }
    (async () => {
      try {
        await axios.post(`${API}/live/confirm`, { session_id: paidSession }, { headers: authHeaders() });
        toast.success('Paiement confirmé, bon live !');
        fetchStatus();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Impossible de confirmer le paiement');
      }
    })();
  }, [fetchStatus]);

  // ---------- spectateur : réception du flux ----------

  const watching = status.is_live && status.has_access && !status.is_coach;

  useEffect(() => {
    if (!watching) {
      setConnState('idle');
      return undefined;
    }
    let closed = false;
    let pc = null;
    let iceServers = FALLBACK_ICE;
    let queue = Promise.resolve();
    let reconnectTimer = null;
    setConnState('connecting');

    const ws = new WebSocket(wsUrl());

    const handle = async (msg) => {
      if (msg.type === 'config') {
        iceServers = msg.ice_servers || FALLBACK_ICE;
      } else if (msg.type === 'offer') {
        if (pc) pc.close();
        pc = new RTCPeerConnection({ iceServers });
        pc.ontrack = (event) => {
          const video = remoteVideoRef.current;
          if (video && event.streams[0]) {
            video.srcObject = event.streams[0];
            video.play().catch(() => {});
            setConnState('playing');
          }
        };
        pc.onicecandidate = (event) => {
          if (event.candidate && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ice', payload: event.candidate }));
          }
        };
        pc.onconnectionstatechange = () => {
          if (pc && pc.connectionState === 'failed') setConnState('failed');
        };
        await pc.setRemoteDescription(msg.payload);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        ws.send(JSON.stringify({ type: 'answer', payload: pc.localDescription }));
      } else if (msg.type === 'ice') {
        if (pc && msg.payload) await pc.addIceCandidate(msg.payload);
      } else if (msg.type === 'waiting') {
        setConnState('waiting');
      } else if (msg.type === 'ended') {
        setConnState('ended');
        fetchStatus();
      } else if (msg.type === 'full') {
        setConnState('full');
      } else if (msg.type === 'denied') {
        setConnState('denied');
        fetchStatus();
      }
    };

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'auth', token: localStorage.getItem('titelli_token'), role: 'viewer' }));
    };
    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      // Les messages sont traités un par un (l'offre doit être prête avant les candidats ICE)
      queue = queue.then(() => handle(msg)).catch((err) => console.error('Live :', err));
    };
    ws.onclose = () => {
      if (closed) return;
      // On garde le message "terminé / complet / refusé" affiché, sinon "connexion perdue"
      setConnState((current) =>
        ['ended', 'full', 'denied'].includes(current) ? current : 'disconnected'
      );
      // Nouvelle tentative : si le coach relance le live ou qu'une place se libère, ça reprend seul
      reconnectTimer = setTimeout(() => {
        if (!closed) setRetry((n) => n + 1);
      }, RECONNECT_MS);
    };

    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      if (pc) pc.close();
      ws.close();
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    };
  }, [watching, retry, fetchStatus]);

  useEffect(() => {
    if (remoteVideoRef.current) remoteVideoRef.current.muted = muted;
  }, [muted, connState]);

  // ---------- paiement ----------

  const handleBuy = async () => {
    if (!localStorage.getItem('titelli_token')) {
      toast.info('Connecte-toi pour accéder au live');
      navigate('/auth');
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/live/checkout`, null, { headers: authHeaders() });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      toast.success('Accès au live débloqué');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Paiement impossible pour le moment');
    } finally {
      setPaying(false);
    }
  };

  // ---------- coach : passer en direct ----------

  const cleanupBroadcast = useCallback(() => {
    clearInterval(previewTimerRef.current);
    previewTimerRef.current = null;
    const ws = wsRef.current;
    wsRef.current = null; // évite que le "onclose" ne croie à une coupure
    if (ws) ws.close();
    peersRef.current.forEach((pc) => pc.close());
    peersRef.current.clear();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    setBroadcasting(false);
    setStarting(false);
  }, []);

  // Si le coach quitte la page, la caméra est coupée (le live reprend s'il revient)
  useEffect(() => () => cleanupBroadcast(), [cleanupBroadcast]);

  const sendPreview = () => {
    const video = localVideoRef.current;
    const ws = wsRef.current;
    if (!video || !video.videoWidth || !ws || ws.readyState !== WebSocket.OPEN) return;
    // Miniature de 32x18 pixels : impossible d'y reconnaître quoi que ce soit, même sans le flou
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 18;
    canvas.getContext('2d').drawImage(video, 0, 0, 32, 18);
    ws.send(JSON.stringify({ type: 'preview', data: canvas.toDataURL('image/jpeg', 0.5) }));
  };

  const connectBroadcaster = () => {
    const ws = new WebSocket(wsUrl());
    wsRef.current = ws;
    let iceServers = FALLBACK_ICE;
    let queue = Promise.resolve();

    const callViewer = async (viewerId) => {
      if (!streamRef.current) return;
      const pc = new RTCPeerConnection({ iceServers });
      peersRef.current.set(viewerId, pc);
      streamRef.current.getTracks().forEach((track) => pc.addTrack(track, streamRef.current));
      pc.onicecandidate = (event) => {
        if (event.candidate && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ice', to: viewerId, payload: event.candidate }));
        }
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      ws.send(JSON.stringify({ type: 'offer', to: viewerId, payload: pc.localDescription }));
    };

    const handle = async (msg) => {
      if (msg.type === 'config') {
        iceServers = msg.ice_servers || FALLBACK_ICE;
      } else if (msg.type === 'ready') {
        if (localVideoRef.current && streamRef.current) {
          localVideoRef.current.srcObject = streamRef.current;
          localVideoRef.current.play().catch(() => {});
        }
        setBroadcasting(true);
        setStarting(false);
        clearInterval(previewTimerRef.current);
        previewTimerRef.current = setInterval(sendPreview, PREVIEW_EVERY_MS);
        setTimeout(sendPreview, 1000);
        fetchStatus();
      } else if (msg.type === 'viewer-joined') {
        await callViewer(msg.viewer_id);
      } else if (msg.type === 'answer') {
        const pc = peersRef.current.get(msg.viewer_id);
        if (pc) await pc.setRemoteDescription(msg.payload);
      } else if (msg.type === 'ice') {
        const pc = peersRef.current.get(msg.viewer_id);
        if (pc && msg.payload) await pc.addIceCandidate(msg.payload);
      } else if (msg.type === 'viewer-left') {
        const pc = peersRef.current.get(msg.viewer_id);
        if (pc) pc.close();
        peersRef.current.delete(msg.viewer_id);
      } else if (msg.type === 'replaced') {
        toast.info('Le live a été repris dans un autre onglet');
        cleanupBroadcast();
      }
    };

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'auth', token: localStorage.getItem('titelli_token'), role: 'broadcaster' }));
    };
    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      queue = queue.then(() => handle(msg)).catch((err) => console.error('Live :', err));
    };
    ws.onclose = () => {
      if (wsRef.current !== ws) return; // arrêt voulu
      toast.error('Connexion du live perdue : relance le direct');
      cleanupBroadcast();
      fetchStatus();
    };
  };

  const parsePrice = () => parseFloat(String(form.price).replace(',', '.')) || 0;

  const startLive = async () => {
    const price = parsePrice();
    if (price < 0 || (price > 0 && price < 0.5)) {
      toast.error('Le prix doit être 0 (gratuit) ou au moins 0.50 CHF');
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('La caméra demande une connexion sécurisée (https, ou localhost sur ton ordinateur)');
      return;
    }
    setStarting(true);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
    } catch (e) {
      toast.error("Impossible d'accéder à la caméra ou au micro : autorise-les dans le navigateur");
      setStarting(false);
      return;
    }
    try {
      await axios.post(`${API}/live/start`, { title: form.title, price }, { headers: authHeaders() });
    } catch (e) {
      stream.getTracks().forEach((track) => track.stop());
      toast.error(e.response?.data?.detail || 'Impossible de démarrer le live');
      setStarting(false);
      return;
    }
    streamRef.current = stream;
    connectBroadcaster();
  };

  const stopLive = async () => {
    cleanupBroadcast();
    try {
      await axios.post(`${API}/live/stop`, null, { headers: authHeaders() });
    } catch (e) {
      toast.error("Le live n'a pas pu être arrêté côté serveur, réessaie");
    }
    fetchStatus();
  };

  const saveSettings = async () => {
    const price = parsePrice();
    try {
      await axios.put(`${API}/live/settings`, { title: form.title, price }, { headers: authHeaders() });
      toast.success('Titre et prix enregistrés');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'enregistrer");
    }
  };

  const uploadOfflineImage = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    try {
      await axios.post(`${API}/live/offline-image`, body, { headers: authHeaders() });
      toast.success('Image enregistrée');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi de l'image");
    } finally {
      event.target.value = '';
    }
  };

  // ---------- affichage ----------

  const isCoach = status.is_coach;
  const showLocal = isCoach && broadcasting;
  const showRemote = !isCoach && status.is_live && status.has_access;
  const showTeaser = !isCoach && status.is_live && !status.has_access;
  const offlineImage = mediaUrl(status.offline_image_url);
  const free = Number(status.price) <= 0;

  const remoteMessages = {
    connecting: 'Connexion au live…',
    waiting: 'Le live démarre dans un instant…',
    disconnected: 'Connexion perdue, nouvelle tentative…',
    ended: 'Le live est terminé. Merci d’avoir été là !',
    full: 'Le live est complet pour le moment.',
    denied: 'Accès refusé : paie pour rejoindre le live.',
    failed: 'Connexion impossible. Ton réseau bloque peut-être le direct.',
  };
  const remoteMessage = remoteMessages[connState];

  return (
    <section className="py-6 sm:py-10 bg-white" data-testid="live-section">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <h2
          className="text-lg sm:text-2xl md:text-3xl font-semibold text-gray-900 mb-4"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Cam Live <span className="text-pink-500">X</span>
        </h2>

        <div className="relative aspect-video rounded-2xl overflow-hidden border border-rose-100 bg-rose-50 shadow-sm">
          {/* Coach : sa propre caméra */}
          {isCoach && (
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className={`absolute inset-0 w-full h-full object-cover ${showLocal ? '' : 'hidden'}`}
            />
          )}

          {/* Spectateur avec accès : le flux net */}
          {showRemote && (
            <video
              ref={remoteVideoRef}
              autoPlay
              muted
              playsInline
              aria-label="Coaching en direct"
              className="absolute inset-0 w-full h-full object-cover bg-black"
            />
          )}

          {/* Pas payé : miniature floutée, jamais la vraie vidéo */}
          {showTeaser && (
            <>
              {status.preview ? (
                <img
                  src={status.preview}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-rose-200 via-rose-100 to-white" />
              )}
              <div className="absolute inset-0 bg-white/30" />
              <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white/90 backdrop-blur rounded-2xl shadow-lg px-5 py-5 sm:px-8 sm:py-6 text-center max-w-sm">
                  <Lock className="w-6 h-6 text-[#D4627F] mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">
                    {status.title || 'Live en cours'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1 mb-4">
                    Le coach est en direct. {free ? 'Rejoins le live gratuitement.' : 'Paie pour voir le cours.'}
                  </p>
                  <button
                    onClick={handleBuy}
                    disabled={paying}
                    className="w-full px-5 py-2.5 bg-[#D4627F] text-white rounded-xl text-sm font-medium hover:bg-[#B84A66] transition-colors disabled:opacity-50"
                    data-testid="live-buy-btn"
                  >
                    {paying
                      ? 'Patiente…'
                      : free
                        ? 'Rejoindre le live'
                        : `Payer ${formatPrice(status.price)} CHF pour regarder`}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Hors ligne : l'image choisie par le coach */}
          {!showLocal && !showRemote && !showTeaser && (
            <>
              {offlineImage ? (
                <img src={offlineImage} alt="Coaching" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-rose-100 via-white to-rose-50" />
              )}
              {!offlineImage && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
                  <VideoOff className="w-8 h-8 text-[#D4627F] mb-2" />
                  <p className="text-gray-700 font-medium">Le prochain live arrive bientôt</p>
                </div>
              )}
              {status.title && (
                <div className="absolute bottom-3 left-3 right-3 sm:left-4 sm:right-auto bg-white/90 rounded-xl px-3 py-1.5 text-sm text-gray-800">
                  Prochain live : {status.title}
                </div>
              )}
            </>
          )}

          {/* Messages de connexion du spectateur */}
          {showRemote && remoteMessage && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-center text-sm px-4">
              {remoteMessage}
            </div>
          )}

          {/* Badge d'état */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {status.is_live || showLocal ? (
              <span className="inline-flex items-center gap-1.5 bg-[#D4627F] text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                EN DIRECT
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 bg-red-500/90 text-white-600 text-xs font-medium px-2.5 py-1 rounded-full">
                <Radio className="w-4 h-4" />
              </span>
            )}
            {(status.is_live || showLocal) && (
              <span className="inline-flex items-center gap-1 bg-white/90 text-gray-700 text-xs px-2.5 py-1 rounded-full">
                <Users className="w-3 h-3" />
                {status.viewer_count}
              </span>
            )}
          </div>

          {/* Son du live */}
          {showRemote && connState === 'playing' && (
            <button
              onClick={() => setMuted((m) => !m)}
              className="absolute bottom-3 right-3 inline-flex items-center gap-2 bg-white/90 hover:bg-white text-gray-800 text-xs font-medium px-3 py-2 rounded-full shadow"
              aria-label={muted ? 'Activer le son' : 'Couper le son'}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              {muted ? 'Activer le son' : 'Son activé'}
            </button>
          )}
          {showRemote && connState === 'failed' && (
            <button
              onClick={() => setRetry((n) => n + 1)}
              className="absolute bottom-3 right-3 bg-white text-gray-800 text-xs font-medium px-3 py-2 rounded-full shadow"
            >
              Réessayer
            </button>
          )}
        </div>

        {/* Panneau du coach */}
        {isCoach && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50/50 p-4 sm:p-5" data-testid="live-coach-panel">
            <p className="text-sm font-semibold text-gray-900 mb-3">Ton espace live</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="sm:col-span-2 text-xs text-gray-600">
                Titre du live
                <input
                  type="text"
                  maxLength={120}
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  disabled={broadcasting}
                  placeholder="Ex : Séance de coaching du jour"
                  className="mt-1 w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#D4627F] disabled:opacity-60"
                />
              </label>
              <label className="text-xs text-gray-600">
                Prix (CHF, 0 = gratuit)
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                  disabled={broadcasting}
                  placeholder="0"
                  className="mt-1 w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#D4627F] disabled:opacity-60"
                />
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              {!broadcasting ? (
                <button
                  onClick={startLive}
                  disabled={starting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4627F] text-white rounded-lg text-sm font-medium hover:bg-[#B84A66] transition-colors disabled:opacity-50"
                  data-testid="live-start-btn"
                >
                  <Video className="w-4 h-4" />
                  {starting ? 'Démarrage…' : 'Passer en direct'}
                </button>
              ) : (
                <button
                  onClick={stopLive}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                  data-testid="live-stop-btn"
                >
                  <VideoOff className="w-4 h-4" />
                  Arrêter le live
                </button>
              )}

              {!broadcasting && (
                <button
                  onClick={saveSettings}
                  className="px-4 py-2 bg-white border border-rose-200 text-[#B84A66] rounded-lg text-sm font-medium hover:bg-rose-50 transition-colors"
                >
                  Enregistrer titre et prix
                </button>
              )}

              <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-rose-200 text-[#B84A66] rounded-lg text-sm font-medium hover:bg-rose-50 transition-colors cursor-pointer">
                <Upload className="w-4 h-4" />
                Image hors ligne
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadOfflineImage} className="hidden" />
              </label>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Reste sur cette page pendant le live : si tu la quittes, la caméra se coupe. Si tu reviens dans les
              6 heures, tu peux relancer le même live et ceux qui ont payé gardent leur accès.
              {status.is_live && !broadcasting && ' Un live est en cours depuis un autre onglet.'}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default LiveStreamSection;
